/**
 * Store Pinia des paramètres de centre (Composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { ICenter } from "../models/center";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { useParameterStore } from "./parameter.store";
import { useCenterStore } from "./center.store";
import { ICenterParameter } from "../models/centerParameter";
import { IParameter } from "../models/parameter";
import { ICenterParameterApiResponse } from "../models/api/centerParameterApiResponse";
import { ICenterParameterForm } from "../models/forms/centerParameterForm";
import { ICenterParameterFilter } from "../models/filters/centerParameterFilter";
import CenterParameterService from "../services/CenterParameterService";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";
import { compareByParameterOrder } from "../helper/parameterOrder";

/**
 * Store Pinia des paramètres de centre utilisant la Composition API
 */
export const useCenterParameterStore = defineStore("centerParameters", () => {
    // State
    const datas = ref<ICenterParameter[]>([]);
    const current = ref<ICenterParameter>({} as ICenterParameter);
    const itemForm = ref<ICenterParameterForm>({} as ICenterParameterForm);
    const isLoading = ref<boolean>(false);
    const loadedCenterId = ref<number | undefined>(undefined);

    // Actions - Hydration
    /**
     * Hydrate CenterParameters from API response
     */
    async function hydrateCenterParameters(centerParams: ICenterParameterApiResponse[]): Promise<ICenterParameter[]> {
        if (centerParams.length === 0) return [];

        // 1. Get stores
        const centerStore = useCenterStore();
        const parameterStore = useParameterStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: ICenterParameter[] = [];
        for (const centerParam of centerParams) {
            const model = { ...centerParam } as unknown as ICenterParameter;

            // Link references (O(1) via Map)
            model.center = centerMap.get(centerParam.centerId) ?? model.center;
            model.parameter = parameterMap.get(centerParam.parameterId) ?? model.parameter;

            models.push(model);
        }

        return models;
    }

    /** Répercute un paramètre modifié sur les associations DÉJÀ hydratées.
     *  `hydrateCenterParameters` fige une référence vers l'objet paramètre au moment du
     *  chargement ; une mise à jour ultérieure (correction d'un code3, resynchro inter-centres)
     *  remplace l'objet dans le store des paramètres, mais les associations continuent de
     *  pointer l'ancien. Comme le store n'est rechargé qu'une fois par centre, une session
     *  ouverte gardait l'ancien référentiel jusqu'au rechargement de la page — de quoi orienter
     *  des doses sur un code de conditionnement périmé. Appelé par le handler `MajParameter`. */
    function applyParameterUpdate(parameter: IParameter) {
        for (const centerParameter of datas.value) {
            if (centerParameter.parameterId === parameter.id) {
                centerParameter.parameter = parameter;
            }
        }
    }

    // Getters
    /** Le référentiel est propre à un centre : une base peut porter les paramètres de PLUSIEURS
     *  centres (celle de STE en héberge cinq). Sans filtre sur le centre, une famille ressort
     *  autant de fois qu'il y a de centres — et les consommateurs qui indexent sur le code3
     *  (grille d'orientation) fusionnent alors des colonnes distinctes.
     *  `loadedCenterId` porte le centre réellement chargé ; tant qu'il est inconnu on ne filtre
     *  pas, pour ne rien masquer à un appelant qui aurait chargé sans centre. */
    const getParameterCritereFamille = computed(() => (code: string, sousCode: string): ICenterParameter[] => {
        return datas.value
            .filter(c => c.parameter?.code1 == code && c.parameter?.code2 == sousCode
                && (loadedCenterId.value === undefined || c.centerId === loadedCenterId.value))
            ?.sort((a, b) => compareByParameterOrder(a.parameter, b.parameter)) || [];
    });

    /** Map parameterId -> code3 des paramètres d'un couple (famille, critère) — ex. résolution
     *  du conditionnement d'une ligne compteur (FK) vers sa colonne du tableau d'orientation. */
    const getParameterCode3ById = computed(() => (code: string, sousCode: string): Map<number, string> => {
        const m = new Map<number, string>();
        for (const cp of getParameterCritereFamille.value(code, sousCode)) {
            m.set(cp.parameterId, cp.parameter?.code3 || '');
        }
        return m;
    });

    const getParameterByIDParameter = computed(() => (idParameter: number, idCenter: number): ICenterParameter | null => {
        // Use ID properties instead of navigation properties to avoid undefined access
        const arrayIDParameter = datas.value.filter(c => c.parameterId == idParameter && c.centerId == idCenter);
        if (arrayIDParameter.length > 0) {
            return arrayIDParameter[0];
        } else {
            return null;
        }
    });

    // Actions

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: ICenterParameterFilter) {
        isLoading.value = true;
        const result = await CenterParameterService.getAll<ICenterParameterFilter, ICenterParameterApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateCenterParameters(result.data as ICenterParameterApiResponse[]);
            loadedCenterId.value = filter.centerId;
        }
        return result;
    }

    /**
     * Récupération d'un paramètre par son identifiant
     * @param id
     */
    async function get(id: number) {
        const result = await CenterParameterService.getById<ICenterParameterApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateCenterParameters([result.data as ICenterParameterApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) {
                datas.value[index] = modelData;
            }

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<ICenterParameter | IDataOperationResult<ICenterParameter>>;
        if (itemForm.value.id > 0) {
            result = await CenterParameterService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateCenterParameters([result.data as ICenterParameterApiResponse]);
                current.value = modelData;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<ICenterParameter>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await CenterParameterService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateCenterParameters([result.data as ICenterParameterApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function deleteItem(elementId: number) {
        const result = await CenterParameterService.delete<ICenterParameter>(elementId);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == elementId);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem(undefined);
            itemForm.value = newForm(0);
        }
        return result;
    }

    function toForm(item: ICenterParameter): ICenterParameterForm {
        const form = {} as ICenterParameterForm;
        form.id = item.id;

        // Use ID properties directly instead of navigation properties
        form.centerId = item.centerId;
        form.parameterId = item.parameterId;
        form.isActive = item.isActive;

        itemForm.value = form;
        return form;
    }

    function newForm(lienId: number): ICenterParameterForm {
        itemForm.value = {
            id: 0,
            centerId: lienId,
            parameterId: 0,
            isActive: true,
        } as ICenterParameterForm;
        return itemForm.value;
    }

    function newItem(lien?: ICenter): ICenterParameter {
        const paramStore = useParameterStore();

        current.value = {
            id: 0,
            centerId: lien?.id ?? 0,
            parameterId: 0,
            center: lien,
            parameter: paramStore.newItem(lien as ICenter),
            isActive: true
        } as ICenterParameter;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        loadedCenterId,
        // Getters
        getParameterCritereFamille,
        getParameterCode3ById,
        getParameterByIDParameter,
        // Actions
        hydrateCenterParameters,
        applyParameterUpdate,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteItem,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajCenterParameter", async (payload: IPayloadStore) => {
    const store = useCenterParameterStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateCenterParameters([payload.data as ICenterParameterApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateCenterParameters([payload.data as ICenterParameterApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});

/** Le PARAMÈTRE lui-même a changé (correction d'un code3, resynchro inter-centres) : l'association
 *  n'a pas bougé, mais elle pointe une référence figée au chargement. Sans cette écoute, une
 *  session déjà ouverte garde l'ancien référentiel jusqu'au rechargement de la page — le store
 *  n'étant rechargé qu'une seule fois par centre.
 *  L'abonnement vit ici, et non dans parameter.store, pour éviter un cycle d'import entre les deux. */
signalRManager.connection.on("MajParameter", (payload: IPayloadStore) => {
    if (payload.action !== "Update" || !payload.data) return;
    useCenterParameterStore().applyParameterUpdate(payload.data as unknown as IParameter);
});